import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BookOpen,
  ArrowLeft,
  MessageSquare,
  Users,
  Briefcase,
  HandCoins,
  Coffee,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CulturalGuide = ({ user }) => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(user?.target_country || "ireland");
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  const sectionIcons = {
    greetings: MessageSquare,
    socializing: Coffee,
    work: Briefcase,
    tipping: HandCoins,
    customs: Users,
  };

  const sectionColors = {
    greetings: "#4A6C6F",
    socializing: "#D97757",
    work: "#4299E1",
    tipping: "#48BB78",
    customs: "#ED8936",
  };

  useEffect(() => {
    fetchGuide();
  }, [selectedCountry]);

  const fetchGuide = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/cultural-guide/${selectedCountry}`, {
        credentials: "include",
      });
      if (response.ok) {
        setGuide(await response.json());
      }
    } catch (error) {
      console.error("Error fetching cultural guide:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#D97757] rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Cultural Integration</span>
            </div>
          </div>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger data-testid="country-select" className="w-48 bg-white border-[#E2E8F0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ireland">🇮🇪 Ireland</SelectItem>
              <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
              <SelectItem value="canada">🇨🇦 Canada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : guide ? (
          <div className="space-y-6">
            {/* Main sections */}
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(guide)
                .filter(([key]) => key !== "customs")
                .map(([key, value]) => {
                  const Icon = sectionIcons[key] || BookOpen;
                  const color = sectionColors[key] || "#4A6C6F";
                  return (
                    <Card
                      key={key}
                      data-testid={`cultural-${key}`}
                      className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl"
                    >
                      <CardHeader>
                        <CardTitle className="flex items-center gap-3 text-[#1A202C] capitalize">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${color}15` }}
                          >
                            <Icon className="w-6 h-6" style={{ color }} />
                          </div>
                          {key}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-[#4A5568] leading-relaxed">{value}</p>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>

            {/* Customs section */}
            {guide.customs && (
              <Card data-testid="cultural-customs" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-[#1A202C]">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${sectionColors.customs}15` }}
                    >
                      <Users className="w-6 h-6" style={{ color: sectionColors.customs }} />
                    </div>
                    Local Customs & Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {guide.customs.map((custom, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-4 p-4 rounded-xl bg-[#F5F5F0]"
                      >
                        <div className="w-8 h-8 bg-[#ED8936]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#ED8936] font-semibold text-sm">{index + 1}</span>
                        </div>
                        <p className="text-[#4A5568]">{custom}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Pro tip */}
            <Card className="bg-[#4A6C6F] border-0 rounded-2xl text-white">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-semibold mb-2">Pro Tip</h4>
                    <p className="text-white/80 leading-relaxed">
                      The best way to learn about local culture is to connect with a Local Homey! They can share
                      real experiences and answer questions that guides can't cover. Head to the Chat section to
                      get matched with someone in {selectedCountry === "ireland" ? "Ireland" : selectedCountry === "uk" ? "the UK" : "Canada"}.
                    </p>
                    <Button
                      data-testid="go-to-chat-btn"
                      onClick={() => navigate("/chat")}
                      className="mt-4 bg-white text-[#4A6C6F] hover:bg-white/90 rounded-full"
                    >
                      Find a Local Homey
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="text-center py-12 text-[#718096]">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No cultural guide available for this country</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default CulturalGuide;
